import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Pie, Bar } from "react-chartjs-2";
import Layout from "../core/Layout";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardData } from "../actions/dashboardDataAction";
import { ListGroup } from "react-bootstrap";
import dayjs from "dayjs";
import Loader from "../core/Loader";
import { useDashboardData, useMonthlyRevenue } from "../component/api/dashboardData";
import { to12HourFormat } from "../utils/common";

const AdminDashboard = () => {
	const [dashboardData, setDashboardData] = useState({});
	const [last7DaysData, setLast7DaysData] = useState([]);
	
	const dispatch = useDispatch();
	const navigate = useNavigate();
	
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const { data: dashboardAPIData, isLoading, error } = useDashboardData(selectedYear);
	const { data: monthlyRevenue } = useMonthlyRevenue(selectedYear); 

	const calculateExpenses = dashboardData?.expenses?.reduce(
		(acc, { grandTotal }) => acc + grandTotal,
		0
	);
	// Data for cards
	const totals = useMemo(() => ({
		appointments: dashboardData.totalappointment,
		patients: dashboardData.patients?.length,
		doctors: dashboardData.doctors?.length,
		staff : dashboardData?.staff?.length,
		expenses: `₹${calculateExpenses?.toFixed(2)}`,
	}) , [dashboardData])

	// Pie chart 
	const userTypeData = useMemo(() => ({
		labels: ["Staff", "Doctor", "Patient"],
		datasets: [
			{
				data: [
					dashboardData.staff?.length,
					dashboardData.doctors?.length,
					dashboardData.patients?.length,
				],
				backgroundColor: ["#667eea", "#764ba2", "#f093fb"],
			},
		],
	}) ,[dashboardData]);

	// Bar chart data for Expenses

	const userLogin = useSelector((state) => state.userLogin);
	const { userInfo } = userLogin;

	// Get available years from API response
	const availableYears = useMemo(() => {
		if (dashboardData.availableYears && dashboardData.availableYears.length > 0) {
			return dashboardData.availableYears;
		}
		return [new Date().getFullYear()];
	}, [dashboardData.availableYears]);

	// Filter appointments by selected year
	const filteredAppointments = useMemo(() => {
		if (!dashboardData.appointments) return [];
		return dashboardData.appointments.filter(app => new Date(app.date).getFullYear() === selectedYear);
	}, [dashboardData.appointments, selectedYear]);

	// Total appointments for selected year
	const totalAppointmentsYearly = filteredAppointments.length;
	useEffect(() => {
		if (dashboardAPIData) {
			setDashboardData(dashboardAPIData?.dashboardData || dashboardAPIData);

			// Calculate last 7 days data
			const today = new Date();
			const last7Days = [...Array(7)].map((_, i) => {
				const date = new Date(today);
				date.setDate(today.getDate() - i);
				return date.toISOString().split("T")[0];
			});

			const expensesByDate = last7Days.map((date) => {
				const total = (dashboardAPIData.dashboardData || dashboardAPIData).expenses
					?.filter((expense) => dayjs(expense.createdAt).format('YYYY-MM-DD') === date)
					.reduce((acc, { grandTotal }) => acc + grandTotal, 0);

				return { date, total: total || 0 };
			});

			setLast7DaysData(expensesByDate.reverse());
		}
	}, [dashboardAPIData]);

	// Bar chart data for Expenses Over Last 7 Days
	const expenseData = {
		labels: last7DaysData.map((data) => data.date),
		datasets: [
			{
				label: "Revenue (₹)",
				data: last7DaysData.map((data) => data.total),
				backgroundColor: "#667eea",
			},
		],
	};

	// Monthly revenue chart data
	const monthlyRevenueData = useMemo(() => {
		return {
			labels: [
				"Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
			],
			datasets: [
				{
					label: "Monthly Revenue (₹)",
					data: monthlyRevenue && monthlyRevenue.map(m => m.total),
					borderColor: "#667eea",
					backgroundColor: "rgba(102, 126, 234, 0.1)",
					fill: true,
					pointRadius: 4,
					pointBackgroundColor: "#667eea",
					tension: 0.4,
					type: 'line',
				},
			],
		};
	}, [monthlyRevenue]);

	
	const gradientStyles = {
		cardGradient: {
			background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
			color: 'white',
			borderRadius: '15px',
			border: 'none',
			boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
			transition: 'all 0.3s ease',
		},
		cardGradientHover: {
			transform: 'translateY(-5px)',
			boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
		},
		whiteCard: {
			backgroundColor: 'white',
			borderRadius: '15px',
			border: 'none',
			boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
		},
		appointmentItem: {
			backgroundColor: 'rgba(102, 126, 234, 0.05)',
			borderRadius: '12px',
			border: '1px solid rgba(102, 126, 234, 0.1)',
			transition: 'all 0.3s ease',
		},
		appointmentItemHover: {
			backgroundColor: 'rgba(102, 126, 234, 0.1)',
			transform: 'translateX(5px)',
		},
		yearSelector: {
			borderRadius: '10px',
			border: '2px solid rgba(102, 126, 234, 0.3)',
			padding: '4px 8px',
			background: 'white',
			color: '#667eea',
			fontWeight: '500',
		}
	};
    
	return (
		<Layout title="Dashboard">
			<>
			{isLoading && <Loader />}
			<div style={{ display: isLoading ? 'none' : 'block', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', padding: '20px' }}>
				{/* Year Selector */}
				<Row className="mb-4">
					<Col md={3}>
						<label htmlFor="yearSelect" className="fw-bold mb-2" style={{ color: '#667eea', fontSize: '1.1rem', paddingRight:'10px' }}>
							📅 Select Year:
						</label>
						<select
							id="yearSelect"
							className="form-select"
							value={selectedYear}
							onChange={e => setSelectedYear(Number(e.target.value))}
							style={gradientStyles.yearSelector}
						>
							{availableYears.map(year => (
								<option key={year} value={year}>{year}</option>
							))}
						</select>
					</Col>
				</Row>

				{/* Top Summary Cards & Recent Appointments */}
				<Row className="g-4 align-items-stretch">
					<Col md={6} lg={3}>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<Card 
								className="shadow-sm h-100" 
								style={{
									backgroundColor: '#90EE90',
									color: 'white',
									borderRadius: '15px',
									border: 'none',
									boxShadow: '0 8px 32px rgba(144, 238, 144, 0.3)',
									cursor: 'pointer',
									transition: 'all 0.3s ease',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = 'translateY(-5px)';
									e.currentTarget.style.boxShadow = '0 12px 40px rgba(144, 238, 144, 0.4)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = 'translateY(0)';
									e.currentTarget.style.boxShadow = '0 8px 32px rgba(144, 238, 144, 0.3)';
								}}
							>
								<Card.Body onClick={() => navigate('/list-patients')} style={{ textAlign: 'center', padding: '25px' }}>
									<div style={{ fontSize: '1.9rem', marginBottom: '10px' }}>👥</div>
									<Card.Title style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '15px' }}>
										Total Patients
									</Card.Title>
									<h2 style={{  fontWeight: 'bold', margin: '0' }}>{totals.patients}</h2>
								</Card.Body>
							</Card>
					
							<Card 
								className="shadow-sm h-100" 
								style={{
									backgroundColor: '#ffc107',
									color: 'white',
									borderRadius: '15px',
									border: 'none',
									boxShadow: '0 8px 32px rgba(255, 193, 7, 0.3)',
									cursor: 'pointer',
									transition: 'all 0.3s ease',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = 'translateY(-5px)';
									e.currentTarget.style.boxShadow = '0 12px 40px rgba(255, 193, 7, 0.4)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = 'translateY(0)';
									e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 193, 7, 0.3)';
								}}
							>
								<Card.Body style={{ textAlign: 'center', padding: '25px' }}>
									<div style={{ fontSize: '1.9rem', marginBottom: '10px' }}>👨‍⚕️</div>
									<Card.Title style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '15px' }}>
										Total Staff
									</Card.Title>
									<h2 style={{ fontWeight: 'bold', margin: '0' }}>{totals.staff}</h2>
								</Card.Body>
							</Card>
						</div>
					</Col>

					<Col md={6} lg={3}>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<Card 
								className="shadow-sm h-100" 
								style={{
									backgroundColor: '#BAC2FF',
									color: 'white',
									borderRadius: '15px',
									border: 'none',
									boxShadow: '0 8px 32px rgba(186, 194, 255, 0.3)',
									cursor: 'pointer',
									transition: 'all 0.3s ease',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = 'translateY(-5px)';
									e.currentTarget.style.boxShadow = '0 12px 40px rgba(186, 194, 255, 0.4)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = 'translateY(0)';
									e.currentTarget.style.boxShadow = '0 8px 32px rgba(186, 194, 255, 0.3)';
								}}
							>
								<Card.Body onClick={() => navigate('/patient-app-details')} style={{ textAlign: 'center', padding: '25px' }}>
									<div style={{ fontSize: '1.9rem', marginBottom: '10px' }}>📅</div>
									<Card.Title style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '15px' }}>
										Total Appointments
									</Card.Title>
									<h2 style={{ fontWeight: 'bold', margin: '0' }}>{totals.appointments}</h2>
								</Card.Body>
							</Card>
					
							<Card 
								className="shadow-sm h-100" 
								style={{
									backgroundColor: '#FE5757',
									color: 'white',
									borderRadius: '15px',
									border: 'none',
									boxShadow: '0 8px 32px rgba(254, 87, 87, 0.3)',
									cursor: 'pointer',
									transition: 'all 0.3s ease',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = 'translateY(-5px)';
									e.currentTarget.style.boxShadow = '0 12px 40px rgba(254, 87, 87, 0.4)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = 'translateY(0)';
									e.currentTarget.style.boxShadow = '0 8px 32px rgba(254, 87, 87, 0.3)';
								}}
							>
								<Card.Body onClick={() => navigate('/list-expenses')} style={{ textAlign: 'center', padding: '25px' }}>
									<div style={{ fontSize: '1.9rem', marginBottom: '10px' }}>💰</div>
									<Card.Title style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '15px' }}>
										Total Revenue
									</Card.Title>
									<h2 style={{  fontWeight: 'bold', margin: '0' }}>{totals.expenses}</h2>
								</Card.Body>
							</Card>
						</div>
					</Col>
					
					<Col md={6}>
						<Card className="shadow-sm h-80" style={gradientStyles.whiteCard}>
							<Card.Body style={{ padding: '25px' }}>
								<Card.Title 
									className="fw-bold mb-3" 
									style={{ 
										color: '#667eea', 
										fontSize: '1.3rem',
										borderBottom: '2px solid rgba(102, 126, 234, 0.1)',
										paddingBottom: '10px'
									}}
								>
									📋 Recent Appointments ({selectedYear})
								</Card.Title>
								<div style={{ maxHeight: '300px', overflowY: 'auto', overflowX:'hidden', scrollbarWidth: 'thin' }}>
									{filteredAppointments.length !== 0 ? 
										filteredAppointments.map((appointment, index) => {
											return (
												<div
													key={index}
													className="d-flex align-items-center justify-content-between p-3 mb-3"
													style={{
														...gradientStyles.appointmentItem,
														cursor: 'pointer',
                            gap:'10px'

													}}
													onClick={() => {
														if (appointment.patient) {
															localStorage.setItem("selectedPatient", JSON.stringify(appointment.patient));
														}
														navigate('/patient-app-details')
													}}
													onMouseEnter={(e) => {
														e.currentTarget.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
														e.currentTarget.style.transform = 'translateX(5px)';
													}}
													onMouseLeave={(e) => {
														e.currentTarget.style.backgroundColor = 'rgba(102, 126, 234, 0.05)';
														e.currentTarget.style.transform = 'translateX(0)';
													}}
												>
													{/* Left Side: Date Box */}
													<div
														className="text-center px-3 py-2 rounded"
														style={{
															background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
															color: 'white',
															minWidth: "60px",
															borderRadius: "10px",
															boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
														}}
													>
														<div className="fw-bold" style={{ fontSize: "1.3rem" }}>
															{new Date(appointment.date).getDate()}
														</div>
														<div style={{ fontSize: "0.8rem", opacity: 0.9 }}>
															{new Date(appointment.date).toLocaleString("default", { month: "short" })}
															{" "} {new Date(appointment.date).getFullYear()}
														</div>
													</div>

													{/* Right Side: Appointment Info */}
													<div className="flex-grow-1 ms-3">
														<div className="fw-bold" style={{ color: '#667eea', fontSize: '1.1rem' }}>
															{appointment?.patient.firstName + "-" + appointment.patient.patientNumber}
														</div>
														<div className="text-muted" style={{ fontSize: '0.9rem' }}>
															🕐 {to12HourFormat(appointment.time)}
														</div>
													</div>

													{/* Icon */}
													<div style={{ color: '#667eea', fontSize: '1.2rem' }}>
														➤
													</div>
												</div>
											)
										})
									: <div style={{ textAlign: 'center', padding: '40px', color: '#667eea' }}>
										<div style={{ fontSize: '3rem', marginBottom: '15px' }}>📋</div>
										<p style={{ fontSize: '1.1rem', margin: '0' }}>No appointments found</p>
									</div>}
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				{/* Charts Section */}
				<Row className="g-4 mt-2">
					<Col md={6}>
						<Card className="shadow-sm h-100" style={gradientStyles.whiteCard}>
							<Card.Body style={{ padding: '25px' }}>
								<Card.Title 
									style={{ 
										color: '#667eea', 
										fontSize: '1.3rem',
										borderBottom: '2px solid rgba(102, 126, 234, 0.1)',
										paddingBottom: '10px',
										marginBottom: '20px',
                    
									}}
								>
									📊 User Type Distribution
								</Card.Title>
								<div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
									<Pie data={userTypeData} options={{ maintainAspectRatio: false }} />
								</div>
							</Card.Body>
						</Card>
					</Col>

					<Col md={6}>
						<Card className="shadow-sm h-100" style={gradientStyles.whiteCard}>
							<Card.Body style={{ padding: '25px' }}>
								<Card.Title 
									style={{ 
										color: '#667eea', 
										fontSize: '1.3rem',
										borderBottom: '2px solid rgba(102, 126, 234, 0.1)',
										paddingBottom: '10px',
										marginBottom: '20px'
									}}
								>
									📈 Revenue Over Time
								</Card.Title>
								<div style={{ height: '300px' }}>
									{monthlyRevenue && monthlyRevenue.length > 0 ? (
										<Bar 
											data={monthlyRevenueData} 
											options={{
												maintainAspectRatio: false,
												plugins: { 
													legend: { 
														display: true,
														labels: { color: '#667eea' }
													} 
												},
												scales: { 
													y: { 
														beginAtZero: true,
														ticks: { color: '#667eea' },
														grid: { color: 'rgba(102, 126, 234, 0.1)' }
													},
													x: {
														ticks: { color: '#667eea' },
														grid: { color: 'rgba(102, 126, 234, 0.1)' }
													}
												},
											}} 
										/>
									) : (
										<div style={{ textAlign: 'center', padding: '40px', color: '#667eea' }}>
											<div style={{ fontSize: '3rem', marginBottom: '15px' }}>📈</div>
											<p style={{ fontSize: '1.1rem', margin: '0' }}>No revenue data available</p>
										</div>
									)}
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</div>
			</>
		</Layout>
	);
};

export default AdminDashboard;